import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import constate from "constate";
import { type ComponentType, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  clamp,
  SubmitCompleteRequestBodySchema,
  type SubmitResponseBody,
} from "shared";
import { useEventCallback } from "usehooks-ts";
import type { ZodTypeAny } from "zod";
import { Api } from "@/api.config";
import type { Step } from "@/components/Stepper";
import { LinkGen, QueryKeys } from "@/LinkGen";
import { AddressVerify } from "@/submit/steps/AddressVerify";
import { StatementUpload } from "@/submit/steps/StatementUpload";
import { StatementVerify } from "@/submit/steps/StatementVerify";
import { SubmitConfirm } from "@/submit/steps/SubmitConfirm";

export type SubmitPov = "customer" | "broker";

type SubmitStep = Step & {
  component: ComponentType;
  schema?: ZodTypeAny;
};

export const steps: SubmitStep[] = [
  { label: "Upload", component: StatementUpload },
  {
    label: "Balance",
    component: StatementVerify,
    schema: SubmitCompleteRequestBodySchema.pick({
      balance: true,
      interestRate: true,
      principal: true,
      interest: true,
      escrow: true,
    }),
  },
  {
    label: "Address",
    component: AddressVerify,
    schema: SubmitCompleteRequestBodySchema.pick({ address: true }),
  },
  {
    label: "Submit",
    component: SubmitConfirm,
    schema: SubmitCompleteRequestBodySchema.pick({ borrower: true }),
  },
];

const [SubmitProvider, useSubmit] = constate(
  (props: { pov: SubmitPov; token: string }) => {
    const queryClient = useQueryClient();
    const [step, setStep] = useState(0);
    const [response, setResponse] = useState<SubmitResponseBody | undefined>();
    const [complete, setComplete] = useState(false);
    const navigate = useNavigate();

    const form = useForm({
      resolver: zodResolver(SubmitCompleteRequestBodySchema),
      mode: "onChange",
      delayError: 600,
    });

    const values = form.watch();

    const back = useEventCallback(() => {
      setStep((prev) => clamp(prev - 1, 0, steps.length - 1));
    });

    const canProceed =
      steps[step].schema === undefined ||
      steps[step].schema.safeParse(values).success;

    const next = useEventCallback(() => {
      if (!canProceed) {
        return;
      }

      setStep((prev) => clamp(prev + 1, 0, steps.length - 1));
    });

    const setName = useEventCallback(
      (name: SubmitResponseBody["extracted"]["borrowers"][number]) => {
        form.setValue("borrower.firstName", name.firstName);
        form.setValue("borrower.lastName", name.lastName);
      },
    );

    const submit = useMutation({
      mutationFn: async (variables: { files: File[] }) => {
        return Api.Submit(variables.files) //
          .then((x) => x.data);
      },
      onSuccess: (res) => {
        setResponse(res);
        const { extracted, uploads } = res;
        // Mortgage overview
        form.setValue("balance", extracted.balance);
        form.setValue("interestRate", extracted.interestRate);
        form.setValue("escrowBalance", extracted.escrowBalance);

        // Monthly payments
        form.setValue("principal", extracted.payment.principal);
        form.setValue("interest", extracted.payment.interest);
        form.setValue("escrow", extracted.payment.escrow);

        // Address
        form.setValue("address.line1", extracted.propertyAddress.line1);
        form.setValue("address.line2", extracted.propertyAddress.line2);
        form.setValue("address.city", extracted.propertyAddress.city);
        form.setValue("address.state", extracted.propertyAddress.state);
        form.setValue("address.zipCode", extracted.propertyAddress.zip_code);
        form.setValue("uploads", uploads);

        if (extracted.borrowers.length === 1) {
          form.setValue("borrower.firstName", extracted.borrowers[0].firstName);
          form.setValue("borrower.lastName", extracted.borrowers[0].lastName);
        }

        setStep((x) => x + 1);
      },
    });

    const handleSubmit = form.handleSubmit(async (values) => {
      const mortgageID = await Api.SubmitComplete(props.token, values);

      if (props.pov === "broker") {
        // Not great BUT await some time for property taxes and value to be fetched
        await new Promise((resolve) => setTimeout(resolve, 10_000));
        await queryClient.invalidateQueries({
          queryKey: QueryKeys.Mortgage.List(),
        });
        navigate(LinkGen.RFQ.Detail(mortgageID));
      }

      setComplete(true);
    });

    const Component = steps[step].component;

    return {
      submit,
      form,
      step,
      back,
      next,
      Component,
      setName,
      response,
      canProceed,
      handleSubmit,
      complete,
      pov: props.pov,
    };
  },
);

export { SubmitProvider, useSubmit };
